import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const PasswordContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-image: url('/bg-green2.png');
  background-size: cover;
  z-index: 1000;
  padding: 2rem;
  overflow-y: auto;
`;

const PasswordText = styled.img`
  width: 70%;
  max-width: 700px;
  height: auto;
  margin-bottom: 2rem;
  object-fit: contain;
  max-height: 100%;
  
  @media (max-width: 768px) {
    width: 85%;
    margin-bottom: 1.5rem;
    max-height: 50vh;
  }
  
  @media (max-width: 480px) {
    width: 90%;
    margin-bottom: 1rem;
  }
`;

const PasswordInputContainer = styled.form`
  display: flex;
  align-items: center;
  background-color: transparent;
  border: 4px solid var(--tes-yellow);
  border-radius: 50px;
  padding: 8px 8px 8px 30px;
  width: 58%;
  max-width: 500px;
  box-shadow: 0 0px 0px rgba(0, 0, 0, 0);
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    width: 85%;
    padding: 6px 6px 6px 20px;
  }
  
  @media (max-width: 480px) {
    width: 90%;
    padding: 4px 4px 4px 16px;
    border-width: 3px;
  }
  
  &.shake {
    animation: shake 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
  }
  
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
    20%, 40%, 60%, 80% { transform: translateX(5px); }
  }
`;

const Input = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  font-size: 1.25rem;
  font-family: var(--font-wingman);
  color: var(--tes-yellow);
  padding: 12px 0;
  width: calc(100% - 60px);
  
  &::placeholder {
    color: var(--tes-yellow);
    opacity: 0.8;
  }
  
  &:focus {
    outline: none;
  }
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    padding: 10px 0;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
    padding: 8px 0;
  }
`;

const ArrowButton = styled.button`
  min-width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  cursor: pointer;
  transition: transform 0.3s ease, background-color 0.3s ease;
  margin-left: auto;
  
  &:hover {
    transform: scale(1.05);
    border: 1px solid var(--tes-yellow);
    background-color: transparent;
  }
  
  i {
    font-size: 1.5rem;
    color: var(--tes-yellow);
  }
  
  @media (max-width: 768px) {
    min-width: 42px;
    height: 42px;
    
    i {
      font-size: 1.25rem;
    }
  }
  
  @media (max-width: 480px) {
    min-width: 36px;
    height: 36px;
    
    i {
      font-size: 1rem;
    }
  }
`;

const ErrorMessage = styled.p`
  color: var(--tes-cream);
  font-size: 0.9rem;
  margin-top: 1rem;
  height: 20px;
  text-align: center;
  max-width: 500px;
  font-family: var(--font-wingman);
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
    margin-top: 0.75rem;
  }
`;

const PasswordScreen = ({ onValidPassword }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);

  // Check localStorage for existing authentication on mount
  useEffect(() => {
    const savedAuth = localStorage.getItem('weddingAuth');
    const savedPartyData = localStorage.getItem('partyData');
    
    if (savedAuth === 'true' && savedPartyData) {
      try {
        JSON.parse(savedPartyData); // Validate JSON
        onValidPassword(); // Skip password screen if valid
      } catch (err) {
        console.error('Error parsing saved party data:', err);
        localStorage.removeItem('weddingAuth');
        localStorage.removeItem('partyData');
      }
    }
  }, [onValidPassword]);

  // Handle password submission with API fetch
  const handleSubmit = async (e) => {
    e.preventDefault();

    const inputPassword = password.toLowerCase().trim();

    try {
      // Fetch party data from the API
      const response = await fetch(`https://api.shaneandtesily.com/api/party/${inputPassword}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok && data.partyName && data.members && data.events) {
        // Successful response, store data in localStorage
        const partyData = {
          partyName: data.partyName,
          members: data.members,
          events: data.events,
          partyId: data.partyId,
          form: inferFormType(data.events), // Infer form type
        };

        localStorage.setItem('weddingAuth', 'true');
        localStorage.setItem('partyData', JSON.stringify(partyData));

        // Call the callback to indicate success
        onValidPassword();
      } else {
        throw new Error(data.error || 'Invalid response from server');
      }
    } catch (err) {
      console.error('Authentication error:', err);
      setAttempts(attempts + 1);
      setError('Incorrect password or server error. Please try again.');
      setPassword('');

      const form = document.getElementById('password-form');
      if (form) {
        form.classList.add('shake');
        setTimeout(() => {
          form.classList.remove('shake');
        }, 500);
      }
    }
  };

  // Temporary function to infer form type from events
  const inferFormType = (events) => {
    if (events.some(event => event.name === 'Rehearsal Dinner')) return 'A';
    if (events.some(event => event.name === 'Ceremony')) return 'B';
    return 'C'; // Default to C if no Ceremony (reception-only)
  };

  return (
    <PasswordContainer>
      <PasswordText src="/password-text.png" alt="Password Required" />
      <PasswordInputContainer id="password-form" onSubmit={handleSubmit}>
        <Input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="ENTER PASSWORD HERE"
          required
          autoFocus
        />
        <ArrowButton type="submit">
          <i className="fas fa-arrow-right"></i>
        </ArrowButton>
      </PasswordInputContainer>
      <ErrorMessage>{error}</ErrorMessage>
    </PasswordContainer>
  );
};

export default PasswordScreen;